import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'hooks/useStore';
import { OOCTabType, FCColorType, OOCForecastAdjustment } from '../oocForecast.type';
import { OOCIntegrationSummary } from 'Interfaces/FDPInterfaces/oocInterfaces';
import { oocService } from 'services/fdp/oocService';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const HISTORY_CONTRACT_TYPE_OPTIONS: { label: string; value: OOCTabType | 'all' }[] = [
  { label: 'All Contract Types', value: 'all' },
  { label: 'MAS DP', value: 'MAS' },
  { label: 'NC DP', value: 'NC' },
  { label: 'Prov. MAS', value: 'ProvMAS' },
  { label: 'Prov. NC', value: 'ProvNC' },
];

export const ROWS_PER_PAGE = 6;

export const TAB_LABELS: Record<OOCTabType, string> = {
  MAS: 'MAS',
  NC: 'NC',
  ProvMAS: 'Prov MAS',
  ProvNC: 'Prov NC',
};

export const TAB_ORDER: OOCTabType[] = ['MAS', 'NC', 'ProvMAS', 'ProvNC'];

export const getDetailFcClass = (colorType: FCColorType, styles: Record<string, string>): string => {
  switch (colorType) {
    case 'new':
      return styles.detailFcNew;
    case 'modified':
      return styles.detailFcModified;
    case 'removed':
      return styles.detailFcRemoved;
    default:
      return styles.detailFcUnchanged;
  }
};

export const useOOCHistoryLogic = () => {
  const { OOCStore } = useStore();
  const {
    historyData, isHistoryLoading, fetchHistory, navigateToDashboard,
    historyMonth, historyYear, setHistoryFilter,
  } = OOCStore;

  const [pendingDate, setPendingDate] = useState<Date>(
    () => new Date(
      OOCStore.historyYear ?? new Date().getFullYear(),
      OOCStore.historyMonth !== null ? OOCStore.historyMonth - 1 : new Date().getMonth(),
      1,
    ),
  );

  const [searchInput, setSearchInput] = useState('');
  const [contractTypeFilter, setContractTypeFilter] = useState<OOCTabType | 'all'>('all');

  // Backend search results — null means "show the date-filtered historyData"
  const [searchResults, setSearchResults] = useState<OOCIntegrationSummary[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    fetchHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced backend search — only the history table updates, not the full page
  useEffect(() => {
    const q = searchInput.trim();
    if (!q) {
      abortRef.current?.abort();
      setSearchResults(null);
      setIsSearching(false);
      return;
    }
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setIsSearching(true);
      try {
        const results = await oocService.getHistory(null, undefined, q);
        if (!ctrl.signal.aborted) setSearchResults(results);
      } catch {
        if (!ctrl.signal.aborted) setSearchResults([]);
      } finally {
        if (!ctrl.signal.aborted) setIsSearching(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  // Source data: backend search results when a query is active, else date-filtered history
  const sourceData = searchResults ?? historyData;

  // Contract type filter applied client-side on top of whichever data source is active
  const filteredHistoryData = useMemo(
    () =>
      contractTypeFilter === 'all'
        ? Array.from(sourceData)
        : Array.from(sourceData).filter((item) => item.contractTypes.includes(contractTypeFilter)),
    // sourceData can be the MobX observable array — include .length so the memo
    // invalidates when items are loaded into it even if the proxy reference stays the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourceData, (sourceData as OOCIntegrationSummary[]).length, contractTypeFilter],
  );

  const recentIntegrations = useMemo(() => filteredHistoryData.slice(0, 3), [filteredHistoryData]);
  const integratedCount = filteredHistoryData.length;

  const [first, setFirst] = React.useState(0);
  const visibleCount = Math.min(ROWS_PER_PAGE, filteredHistoryData.length - first);

  const handleRowClick = (oocId: string) => {
    OOCStore.fetchHistoryDetail(oocId);
  };

  const handleApplyFilter = () => {
    const month = pendingDate.getMonth() + 1;
    const year = pendingDate.getFullYear();
    setFirst(0);
    setSearchInput('');
    setSearchResults(null);
    setHistoryFilter(month, year);
  };

  const handleShowYTD = () => {
    if (historyYear !== null) {
      setFirst(0);
      setSearchInput('');
      setSearchResults(null);
      setHistoryFilter(null, historyYear);
    }
  };

  const handleClearFilter = () => {
    setFirst(0);
    setSearchInput('');
    setSearchResults(null);
    setHistoryFilter(null, null);
  };

  const selectedMonthLabel = historyMonth !== null ? (MONTH_NAMES[historyMonth - 1] ?? '') : null;

  return {
    OOCStore,
    filteredHistoryData,
    isHistoryLoading,
    isSearching,
    navigateToDashboard,
    recentIntegrations,
    integratedCount,
    first,
    setFirst,
    visibleCount,
    handleRowClick,
    historyMonth,
    historyYear,
    pendingDate,
    setPendingDate,
    handleApplyFilter,
    handleShowYTD,
    handleClearFilter,
    selectedMonthLabel,
    searchInput,
    setSearchInput,
    contractTypeFilter,
    setContractTypeFilter,
  };
};

export const useHistoryDetailLogic = () => {
  const { OOCStore } = useStore();
  const { historyDetailSubmission, isHistoryDetailLoading, closeHistoryDetail } = OOCStore;

  const [activeTab, setActiveTab] = useState<OOCTabType>('MAS');

  const visible = historyDetailSubmission !== null || isHistoryDetailLoading;

  const tabGroups = useMemo(() => {
    if (!historyDetailSubmission) return {} as Partial<Record<OOCTabType, OOCForecastAdjustment[]>>;
    const groups: Partial<Record<OOCTabType, OOCForecastAdjustment[]>> = {};
    TAB_ORDER.forEach((tab) => {
      const filtered = historyDetailSubmission.adjustments.filter((a) => a.tab === tab);
      if (filtered.length > 0) groups[tab] = filtered;
    });
    return groups;
  }, [historyDetailSubmission]);

  const availableTabs = Object.keys(tabGroups) as OOCTabType[];
  const currentTab = availableTabs.includes(activeTab) ? activeTab : availableTabs[0] ?? 'MAS';
  const currentAdjs = tabGroups[currentTab] ?? [];
  const periods = currentAdjs[0]?.forecasts.map((f) => f.period) ?? [];
  const showProbability = currentTab === 'ProvMAS';

  return {
    historyDetailSubmission,
    isHistoryDetailLoading,
    closeHistoryDetail,
    visible,
    activeTab,
    setActiveTab,
    tabGroups,
    availableTabs,
    currentTab,
    currentAdjs,
    periods,
    showProbability,
  };
};
